/** THE FIRST BROADCAST: end-of-training mini report + the "Skip training?" dialog. */
import '../../tutorial/tutorial.css';
import { h } from '../dom';
import type { ScreenCtx, TrainingReportParams } from './context';
import { categoryBars, letterBadge, listNav, type ScreenInstance } from './common';

const headset = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 13v-1a8 8 0 0 1 16 0v1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><rect x="2.6" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/><rect x="17.2" y="12.5" width="4.2" height="6.5" rx="1.6" fill="currentColor"/></svg>`;
const avatar = () => h('span', { class: 'sd-pd__avatar', html: headset });

const SKILLS: { label: string; lessons: string[] }[] = [
  { label: 'Cut to air with the number keys', lessons: ['program'] },
  { label: 'Wide for play, tight for the strike', lessons: ['tight', 'wide'] },
  { label: 'Replays at a stoppage, from two angles', lessons: ['replay', 'angle', 'live'] },
  { label: 'Scorebug up, lower thirds on cue', lessons: ['scorebug', 'lower3'] },
  { label: 'Give the booth what it asks for', lessons: ['booth'] },
  { label: 'Never miss a goal — then go tight', lessons: ['build', 'goal'] },
  { label: 'Replay the goal, name the scorer', lessons: ['goalreplay', 'goalangle', 'goallive', 'scorer'] },
];

export function trainingReportScreen(ctx: ScreenCtx, p: TrainingReportParams): ScreenInstance {
  const r = p.report;
  const learned = new Set(p.learned);
  const toTitle = () => ctx.show('title', { focus: 'daily' });
  const again = () => ctx.startTraining();

  const skills = SKILLS.map((s) => {
    const ok = s.lessons.some((id) => learned.has(id));
    return h('div', { class: `sd-tr__skill ${ok ? '' : 'is-missed'}` }, [h('span', { class: 'sd-tr__tick', text: ok ? '✓' : '–' }), h('span', { text: s.label })]);
  });
  const mins = Math.max(1, Math.round(p.seconds / 60));

  const cont = h('button', { class: 'sd-btn sd-btn--primary sd-btn--golive', attrs: { type: 'button', 'data-action': 'training-continue' }, on: { click: toTitle } },
    [h('span', { class: 'sd-golive__dot' }), 'ON TO MATCHDAY', h('kbd', { text: 'ENTER' })]);
  const el = h('section', { class: 'sd-screen sd-screen--overlay sd-tr', attrs: { 'data-screen': 'training-report' } }, [
    h('div', { class: 'sd-tr__card' }, [
      h('div', { class: 'sd-tr__kicker' }, [h('span', { class: 'sd-rec' }), h('span', { text: `TRAINING COMPLETE · ${mins} MIN` })]),
      h('h1', { class: 'sd-tr__title', text: 'THE FIRST BROADCAST' }),
      h('p', { class: 'sd-tr__sub', text: 'Rookie grading — the real thing is stricter. Every match gets a report like this, with the reasons behind every point.' }),
      h('div', { class: 'sd-tr__grid' }, [
        h('div', { class: 'sd-tr__grade' }, [
          letterBadge(r.letter, 'sd-letter--xl'),
          h('div', { class: 'sd-tr__rating', text: `${Math.round(r.rating)}` }),
          h('div', { class: 'sd-field__label', text: 'DIRECTOR RATING' }),
        ]),
        h('div', { class: 'sd-tr__learned' }, [h('h3', { text: 'YOU CAN NOW' }), ...skills]),
        h('div', { class: 'sd-tr__bars' }, [h('h3', { text: 'HOW IT WAS GRADED' }), categoryBars(r.byCategory)]),
      ]),
      h('div', { class: 'sd-tr__quote' }, [avatar(), h('span', { text: "Today's Daily Match is the same fixture for everyone. Go and top the gallery." })]),
      h('div', { class: 'sd-tr__actions' }, [
        h('button', { class: 'sd-btn', attrs: { type: 'button', 'data-action': 'training-again' }, on: { click: again } }, ['RUN IT AGAIN']),
        cont,
      ]),
    ]),
  ]);
  const buttons = () => Array.from(el.querySelectorAll<HTMLElement>('.sd-tr__actions button'));
  return {
    el, overlay: true,
    mounted() { cont.focus({ preventScroll: true }); },
    onKey(e) {
      if (e.key === 'Escape') { toTitle(); return true; }
      return listNav(buttons, e, 'x');
    },
  };
}

export function trainingSkipScreen(ctx: ScreenCtx): ScreenInstance {
  const resume = () => { ctx.show('none'); ctx.deps.onResume?.(); };
  const skip = () => { ctx.deps.onSkipTraining?.(); ctx.show('title'); };
  const btn = (label: string, key: string | null, cls: string, fn: () => void, action: string) =>
    h('button', { class: `sd-btn ${cls}`, attrs: { type: 'button', 'data-action': action }, on: { click: fn, mouseenter: (e) => (e.currentTarget as HTMLElement).focus({ preventScroll: true }) } },
      [h('span', { text: label }), key ? h('kbd', { text: key }) : null]);
  const list = h('div', { class: 'sd-skip__list' }, [
    btn('KEEP TRAINING', 'ESC', 'sd-btn--primary', resume, 'training-resume'),
    btn('SKIP TO THE MENU', null, '', skip, 'training-skip'),
  ]);
  const el = h('section', { class: 'sd-screen sd-screen--overlay sd-skip', attrs: { 'data-screen': 'training-skip' } }, [
    h('div', { class: 'sd-skip__card' }, [
      h('div', { class: 'sd-skip__head' }, [avatar(), h('h1', { class: 'sd-skip__title', text: 'SKIP TRAINING?' })]),
      h('p', { class: 'sd-skip__p', text: 'The First Broadcast takes about four minutes and covers every control you need. You can run it again any time from the title screen.' }),
      list,
    ]),
  ]);
  const buttons = () => Array.from(list.querySelectorAll<HTMLElement>('button'));
  return {
    el, overlay: true,
    mounted() { buttons()[0]?.focus({ preventScroll: true }); },
    onKey(e) {
      if (e.key === 'Escape') { resume(); return true; }
      return listNav(buttons, e);
    },
  };
}
